import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import {
  PageTitle, Card, Section, Btn, Pill, Kpi,
  Table, Th, Td, EmptyState, fmt,
} from '../components/ui-kit';
import { Banknote, Copy } from 'lucide-react';

interface Invoice {
  id: string;
  number?: string;
  amount_eur?: number;
  amount_ttc?: number;
  status?: string;
  pdf_url?: string;
  created_at?: string;
  client_name?: string;
}

const SEPA = {
  beneficiary: 'XYQAR SAS',
  iban: '',
  bic:  '',
  bank: 'Coordonnées bancaires en cours de finalisation',
};

export default function Invoices() {
  const { getToken } = useXyqarAuth();
  const [items, setItems] = React.useState<Invoice[] | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const tok = await getToken().catch(() => '');
        const j = await apiGet<{ invoices: Invoice[] }>('/api/v1/exec/invoices', tok);
        setItems(j.invoices ?? []);
      } catch { setItems([]); }
    })();
  }, [getToken]);

  const total = (items ?? []).reduce((s, i) => s + Number(i.amount_eur ?? i.amount_ttc ?? 0), 0);
  const paid  = (items ?? []).filter((i) => i.status === 'paid').length;
  const due   = (items ?? []).filter((i) => i.status !== 'paid').length;

  const copy = (s: string) => navigator.clipboard.writeText(s).catch(() => {});

  return (
    <div>
      <PageTitle
        kicker="Executive Room"
        title="Invoices"
        right={
          <div className="flex items-center gap-2">
            <Pill tone="warn"><Banknote className="h-3 w-3" /> manual finance ops until bank publish</Pill>
          </div>
        }
      />

      <Section title="At a glance">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi accent="blue"  label="Invoices total"  value={fmt(items?.length)} />
          <Kpi accent="green" label="Paid"            value={fmt(paid)} />
          <Kpi accent="amber" label="Outstanding"     value={fmt(due)} />
          <Kpi              label="Total amount €"  value={fmt(Math.round(total))} />
        </div>
      </Section>

      <Section
        title="Bank transfer status"
        hint="Le modèle économique n'utilise pas Stripe. Tant que les coordonnées bancaires finales ne sont pas publiées ici, le paiement doit être confirmé avec Xyqar Finance via la facture PDF et le canal contractuel."
      >
        <Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <KV label="Bénéficiaire" value={SEPA.beneficiary} onCopy={() => copy(SEPA.beneficiary)} />
            <KV label="Banque"       value={SEPA.bank} />
            <KV label="IBAN"         value="À publier dans l'Executive Room après finalisation banque" />
            <KV label="BIC / SWIFT"  value="À publier dans l'Executive Room après finalisation banque" />
          </div>
          <p className="text-[11px] text-white/40 mt-4 font-mono leading-relaxed">
            Référence de paiement à confirmer avec Xyqar Finance : <span className="text-white">numéro de facture</span>.
            Les statuts <Pill tone="live">paid</Pill> restent rapprochés manuellement tant que le portail ne diffuse pas les coordonnées bancaires définitives.
          </p>
        </Card>
      </Section>

      <Section title="Issued invoices">
        {items === null && <Card>Loading…</Card>}
        {items && items.length === 0 && (
          <EmptyState
            title="No invoices yet"
            hint="Invoices appear here once a CRA is approved or a contract is signed."
          />
        )}
        {items && items.length > 0 && (
          <Table>
            <thead>
              <tr><Th>#</Th><Th>Client</Th><Th>Status</Th><Th>Created</Th><Th className="text-right">Amount €</Th><Th></Th></tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-white/5 hover:bg-white/5">
                  <Td><span className="text-white font-mono text-xs">{i.number ?? i.id}</span></Td>
                  <Td><span className="text-white/80 text-xs">{i.client_name ?? '—'}</span></Td>
                  <Td><Pill tone={i.status === 'paid' ? 'live' : 'warn'}>{i.status ?? '—'}</Pill></Td>
                  <Td><span className="text-[11px] text-white/40 font-mono">{i.created_at?.slice(0, 10) ?? '—'}</span></Td>
                  <Td className="text-right"><span className="text-white font-mono">{Number(i.amount_eur ?? i.amount_ttc ?? 0).toLocaleString('fr-FR')}</span></Td>
                  <Td className="text-right">{i.pdf_url && <Btn as="a" href={i.pdf_url}>PDF</Btn>}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </div>
  );
}

function KV({ label, value, onCopy }: { label: string; value: string; onCopy?: () => void }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-white/5">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">{label}</p>
        <p className="text-white text-sm font-mono mt-1 break-all">{value}</p>
      </div>
      {onCopy && (
        <button onClick={onCopy} className="text-white/40 hover:text-white p-1 rounded" title="Copy">
          <Copy className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
